import type { Experiment, Variant } from 'types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { IconAlertTriangle, IconCircleCheck, IconInfoCircle } from '@tabler/icons-react';
import { cn } from 'helpers/cn';
import { formatNumber } from 'helpers/formatters';
import { checkSampleRatio, type VariantAllocation } from 'helpers/sample-ratio';

/**
 * Did traffic split the way it was configured?
 *
 * Placed above the results because it decides whether anything below them is worth
 * reading. If assignment is broken (a variant failing to apply, a redirect dropping
 * visitors, bot filtering hitting one arm harder) the two groups are not comparable
 * and every p-value on the page is measuring that fault instead of the change.
 *
 * Deliberately quiet when healthy: a single line, no chart. A validity check that
 * takes up as much room as the results themselves gets ignored.
 */
type SampleRatioCheckProps = {
  experiment: Experiment;
  manager: ReturnType<typeof import('@/state/experiments/useExperimentStats').useExperimentStats>;
};

const pct = (value: number) => `${(value * 100).toFixed(1)}%`;

export const SampleRatioCheck = ({ experiment, manager }: SampleRatioCheckProps) => {
  const { statistics } = manager;
  if (!statistics) return null;

  // Deployed variants are excluded: a rollout is not a randomised arm, so its share
  // of traffic is not something the configured split predicts.
  const allocations: VariantAllocation[] = (experiment.variants ?? [])
    .filter((v: Variant) => !v.deployed)
    .map((v: Variant) => ({
      id: v.id,
      name: v.name,
      weight: v.weight ?? 0,
      sessions: statistics[v.id]?.sessions ?? 0,
    }));

  const result = checkSampleRatio(allocations);

  if (result.state === 'not-applicable') {
    return (
      <TooltipProvider>
        <p className="supporting flex items-center gap-1.5" data-tour="results-traffic-split">
          <IconInfoCircle className="size-3.5 shrink-0" />
          Traffic split not checked: {result.reason}
        </p>
      </TooltipProvider>
    );
  }

  const healthy = result.state === 'ok';

  return (
    <TooltipProvider>
      {healthy ? (
        // One line when nothing is wrong, with the detail behind a hover.
        <Tooltip>
          <TooltipTrigger asChild>
            <p
              className="border-line bg-panel2 rounded-card flex cursor-help items-center gap-2 border px-3 py-2"
              data-tour="results-traffic-split"
            >
              <IconCircleCheck className="text-ok size-4 shrink-0" />
              <span className="micro-label text-ok">Traffic split ok</span>
              <span className="supporting">matches the configured weights</span>
              <IconInfoCircle className="text-dim size-3 shrink-0" />
            </p>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm space-y-2">
            <p>
              A chi-square goodness-of-fit test against the configured split, p = {result.pValue.toFixed(4)}. A mismatch
              would mean the groups are not comparable and nothing below could be trusted.
            </p>
            <div className="space-y-0.5">
              {result.rows.map((row) => (
                <p key={row.id} className="num">
                  {row.name}: {pct(row.actualShare)} vs {pct(row.expectedShare)} expected
                </p>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      ) : (
        // A split that does not match the configuration is a broken measurement, not a
        // decision to make, so it takes danger rather than warn.
        <div
          className="border-danger-line bg-danger-bg rounded-card flex gap-3 border px-4 py-3.5"
          data-tour="results-traffic-split"
        >
          <IconAlertTriangle className="text-danger mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p className="micro-label text-danger">Traffic did not split as configured</p>
            <p className="text-foreground text-body leading-relaxed">
              The observed split is further from the configured weights than chance explains (p ={' '}
              {result.pValue < 0.0001 ? '<0.0001' : result.pValue.toFixed(4)}). Until that is explained, the variants
              may not be comparable and the results below could be measuring the assignment fault rather than the
              change. Common causes are a variant failing to apply, a redirect losing visitors, or filtering that hits
              one arm harder.
            </p>
            <div className="space-y-1 pt-1">
              {result.rows.map((row) => (
                <div key={row.id} className="num flex flex-wrap items-baseline gap-x-2">
                  <span className="text-foreground">{row.name}</span>
                  <span className="text-dim">
                    {formatNumber(row.sessions)} sessions: {pct(row.actualShare)} of traffic, {pct(row.expectedShare)}{' '}
                    expected
                  </span>
                  <span className={cn(Math.abs(row.deviation) > 0.02 ? 'text-danger' : 'text-dim')}>
                    ({row.deviation >= 0 ? '+' : ''}
                    {(row.deviation * 100).toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </TooltipProvider>
  );
};
